import _ from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import box from '~/assets/icons/box_2.svg';
import rubleRound from '~/assets/icons/currency_ruble_round.svg';
import pack from '~/assets/icons/pack.svg';
import user from '~/assets/icons/user_2.svg';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { Text } from '~/components/ui/Text';
import { rage, useObservable } from '~/rage';
import {
	InventoryCell as InventoryCellItem,
	InventoryType as InventoryCellType,
	InventoryType,
	Item,
	ItemAction,
	WalletType,
} from '~/shared';
import { useInventoryCells } from '../../hooks/useInventoryCells';
import {
	resetExchangeState,
	setConfirmationVisible,
	useLayoutDispatch,
} from '../../redux';
import { INVENTORY_CELLS_COUNT } from '../Inventory';
import { InventoryCell } from '../InventoryCell';
import { MoneyInput } from '../MoneyInput';
import {
	StyledContainer,
	StyledCountContainer,
	StyledExchangeContainer,
	StyledExchangeRightContainer,
	StyledExhangeLeftContainer,
	StyledInventoryGrid,
	StyledMoneyContainer,
	StyledPlayerInfoLeft,
	StyledPlayerInfoMoney,
	StyledPlayerInfoRight,
	StyledSection,
	StyledSectionHeading,
} from './InventoryExchange.styled';

const InventoryExchange = () => {
	const dispatch = useLayoutDispatch();
	const {
		inventoryIds,
		inventoryCells,
		backpackCells,
		fromExchangeCells,
		toExchangeCells,
		hasBackpack,
		fromExchangeInventory,
	} = useInventoryCells();
	const ready = useObservable(rage.inventory.getIsPlayerReady());
	const setReady = (value: boolean) =>
		rage.inventory.setIsPlayerReady(value).catch(() => undefined);
	const [buttonHover, setButtonHover] = useState(false);
	const secondPlayerReady = useObservable(
		rage.inventory.getIsSecondPlayerReady(),
	);
	const [dragging, setDragging] = useState(false);
	const [draggedItem, setDraggedItem] = useState<{
		cell: InventoryCellItem;
		sourceType: InventoryCellType;
	} | null>(null);

	const nickname = useObservable(rage.player.getNickname());
	const characterId = useObservable(rage.player.getCharacterId());
	const playerLevel = useObservable(rage.player.getPlayerLevel());
	const level = playerLevel?.level ?? 0;
	const balance = useObservable(rage.bank.getBalance(WalletType.Cash));
	const otherPlayerInfo = useObservable(rage.inventory.getOtherPlayerInfo());
	const exchangeToMoneyAmount = useObservable(
		rage.inventory.getExchangeMoneyAmount(),
	);
	const isExchangeCompleted = useObservable(
		rage.inventory.getIsExchangeCompleted(),
	);
	const cashBalance = useObservable(rage.bank.getBalance(WalletType.Cash));
	const maxExchangeMoneyAmount = useObservable(
		rage.inventory.getMaxExchangeMoneyAmount(),
	);
	const isExchangeCompletedRef = useRef(isExchangeCompleted);
	const [exchangeMoneyAmount, setExchangeMoneyAmount] = useState(0);

	const tradeButtonText = useMemo(() => {
		if (!ready) return 'Подтвердите готовность обмена';
		if (ready && !secondPlayerReady) return 'Ожидание готовности игрока';
		return 'Подтвердить обмен';
	}, [ready, secondPlayerReady]);

	const throttledSetExchangeMoneyAmount = useMemo(
		() =>
			_.throttle(async (amount: number) => {
				const result = await rage.inventory
					.setExchangeMoneyAmount(amount)
					.catch(() => undefined);
				if (result !== undefined && result !== exchangeMoneyAmount) {
					setExchangeMoneyAmount(result);
				}
			}, 1000),
		[],
	);

	const handleExchangeMoneyAmountChange = async (value: number) => {
		setExchangeMoneyAmount(value);
		await throttledSetExchangeMoneyAmount(value);
	};

	const handleDragStart = (
		e: React.DragEvent,
		cell: InventoryCellItem,
		sourceType: InventoryCellType,
	) => {
		if (!cell.item || ready) {
			e.preventDefault();
			return;
		}
		setDragging(true);
		setDraggedItem({ cell, sourceType });
		e.dataTransfer.effectAllowed = 'move';
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = 'move';
		setDragging(true);
	};

	const handleDragEnd = () => {
		setDraggedItem(null);
		setDragging(false);
	};

	const handleDrop = (
		e: React.DragEvent,
		targetCell: InventoryCellItem,
		targetType: InventoryCellType,
	) => {
		e.preventDefault();
		if (!draggedItem) return;

		const { cell: sourceCell, sourceType } = draggedItem;

		if (!sourceCell.item) return;

		const getInventoryIdByType = (
			type: InventoryCellType,
		): string | undefined => {
			switch (type) {
				case InventoryCellType.PLAYER:
					return inventoryIds[InventoryType.PLAYER];
				case InventoryCellType.BACKPACK:
					return inventoryIds[InventoryType.BACKPACK];
				case InventoryCellType.EXCHANGE:
					return fromExchangeInventory?.id;
				default:
					return undefined;
			}
		};

		const sourceInventoryId = getInventoryIdByType(sourceType);
		const targetInventoryId = getInventoryIdByType(targetType);

		if (!sourceInventoryId || !targetInventoryId) return;

		const sourceSlot = sourceCell.position - 1;
		const targetSlot = targetCell.position - 1;

		if (sourceType === targetType) {
			if (sourceSlot === targetSlot) return;
			rage.inventory
				.moveItem(
					sourceInventoryId,
					sourceCell.item.id,
					sourceCell.item.count,
					sourceCell.item.data,
					sourceSlot,
					targetSlot,
				)
				.catch(() => undefined);
		} else {
			rage.inventory
				.transferItem(
					sourceCell.item.id,
					sourceCell.item.count,
					sourceCell.item.data,
					sourceInventoryId,
					sourceSlot,
					targetInventoryId,
					targetSlot,
				)
				.catch(() => undefined);
		}
	};

	const getAvailableActions = (item: Item) => {
		if (!item.stackable || item.count <= 1) {
			return [];
		}
		return [ItemAction.SPLIT];
	};

	const handleItemAction = (
		action: ItemAction,
		cell: InventoryCellItem,
		cellType: InventoryCellType,
	) => {
		if (!cell.item) return;

		const inventoryId = (() => {
			switch (cellType) {
				case InventoryCellType.PLAYER:
					return inventoryIds[InventoryType.PLAYER];
				case InventoryCellType.BACKPACK:
					return inventoryIds[InventoryType.BACKPACK];
				case InventoryCellType.EXCHANGE:
					return fromExchangeInventory?.id;
				default:
					return undefined;
			}
		})();

		if (!inventoryId) return;

		rage.inventory
			.performItemAction(
				inventoryId,
				cell.item.id,
				cell.item.count,
				cell.item.data,
				cell.position - 1,
				action,
			)
			.catch(() => undefined);
	};

	const handleExchange = () => {
		if (!ready || !secondPlayerReady) {
			return;
		}

		dispatch(setConfirmationVisible(true));
	};

	useEffect(() => {
		if (!ready || !secondPlayerReady) dispatch(setConfirmationVisible(false));
	}, [ready, secondPlayerReady]);

	useEffect(() => {
		isExchangeCompletedRef.current = isExchangeCompleted;
	}, [isExchangeCompleted]);

	useEffect(() => {
		dispatch(resetExchangeState());
		return () => {
			dispatch(resetExchangeState());
			if (!isExchangeCompletedRef.current) {
				rage.inventory.cancelExchange().catch(() => undefined);
			}
		};
	}, []);

	return (
		<StyledContainer>
			<StyledPlayerInfoLeft>
				<Text style={{ fontSize: 20, color: '#ffffff' }}>{nickname}</Text>
				<Text
					style={{
						fontSize: 14,
						color: '#ffffff',
						opacity: 0.25,
						marginTop: 2,
					}}
				>
					{level} Ур. ID: {characterId}
				</Text>
				<StyledPlayerInfoMoney>
					<img src={rubleRound} />
					<Text style={{ fontSize: 14, fontWeight: 500, color: '#ffffff' }}>
						{balance.toLocaleString('ru-RU')}
					</Text>
				</StyledPlayerInfoMoney>
			</StyledPlayerInfoLeft>
			<StyledPlayerInfoRight>
				<Text style={{ fontSize: 20, color: '#ffffff' }}>
					{otherPlayerInfo?.nickname}
				</Text>
				<Text
					style={{
						fontSize: 14,
						color: '#ffffff',
						opacity: 0.25,
						marginTop: 2,
					}}
				>
					{otherPlayerInfo?.level} Ур. ID: {otherPlayerInfo?.serverPlayerId}
				</Text>
			</StyledPlayerInfoRight>
			<StyledExchangeContainer>
				<StyledExhangeLeftContainer>
					<StyledSection>
						<StyledSectionHeading>
							<img src={box} />
							<Text style={{ fontSize: 14, fontWeight: 500, color: '#ffffff' }}>
								Мой инвентарь
							</Text>
							<StyledCountContainer>
								<Text>
									<span>
										{inventoryCells.filter((cell) => cell.item).length}
									</span>{' '}
									/ {INVENTORY_CELLS_COUNT}
								</Text>
								<img src={pack} />
							</StyledCountContainer>
						</StyledSectionHeading>
						<StyledInventoryGrid>
							{inventoryCells.map((cell, index) => (
								<InventoryCell
									key={`${cell.id}-${index}`}
									item={
										cell.item
											? {
													...cell.item,
													availableActions: getAvailableActions(cell.item),
											  }
											: undefined
									}
									isDragging={dragging}
									onDragStart={(e) =>
										handleDragStart(e, cell, InventoryCellType.PLAYER)
									}
									onDragOver={handleDragOver}
									onDrop={(e) => handleDrop(e, cell, InventoryCellType.PLAYER)}
									onDragEnd={handleDragEnd}
									onAction={(action) =>
										handleItemAction(action, cell, InventoryCellType.PLAYER)
									}
								/>
							))}
						</StyledInventoryGrid>
					</StyledSection>
					<StyledSection>
						<StyledSectionHeading>
							<img src={box} />
							<Text style={{ fontSize: 14, fontWeight: 500, color: '#ffffff' }}>
								Мой рюкзак
							</Text>
							{hasBackpack ? (
								<StyledCountContainer>
									<Text>
										<span>
											{backpackCells.filter((cell) => cell.item).length}
										</span>{' '}
										/ {backpackCells.filter((cell) => !cell.isClosed).length}
									</Text>
									<img src={pack} />
								</StyledCountContainer>
							) : (
								<Text style={{ fontSize: 16, color: '#ffffff', opacity: 0.25 }}>
									Рюкзак отсутствует
								</Text>
							)}
						</StyledSectionHeading>
						<StyledInventoryGrid>
							{backpackCells.map((cell, index) => (
								<InventoryCell
									key={`${cell.id}-${index}`}
									item={
										cell.item
											? {
													...cell.item,
													availableActions: getAvailableActions(cell.item),
											  }
											: undefined
									}
									closed={!hasBackpack || cell.isClosed}
									isDragging={dragging}
									onDragStart={(e) =>
										handleDragStart(e, cell, InventoryCellType.BACKPACK)
									}
									onDragOver={handleDragOver}
									onDrop={(e) =>
										handleDrop(e, cell, InventoryCellType.BACKPACK)
									}
									onDragEnd={handleDragEnd}
									onAction={(action) =>
										handleItemAction(action, cell, InventoryCellType.BACKPACK)
									}
								/>
							))}
						</StyledInventoryGrid>
					</StyledSection>
				</StyledExhangeLeftContainer>
				<StyledExchangeRightContainer>
					<StyledSection>
						<StyledSectionHeading>
							<img src={box} />
							<Text style={{ fontSize: 14, fontWeight: 500, color: '#ffffff' }}>
								Обмен
							</Text>
							<Text
								style={{
									fontSize: 16,
									color: '#ffffff',
									opacity: 0.25,
									fontStyle: 'italic',
								}}
							>
								(Мое предложение)
							</Text>
						</StyledSectionHeading>
						<StyledInventoryGrid>
							{fromExchangeCells.map((cell, index) => (
								<InventoryCell
									key={`${cell.id}-${index}`}
									item={
										cell.item
											? {
													...cell.item,
													availableActions: getAvailableActions(cell.item),
											  }
											: undefined
									}
									isDragging={dragging}
									onDragStart={(e) =>
										handleDragStart(e, cell, InventoryCellType.EXCHANGE)
									}
									onDragOver={handleDragOver}
									onDrop={(e) =>
										handleDrop(e, cell, InventoryCellType.EXCHANGE)
									}
									onDragEnd={handleDragEnd}
									onAction={(action) =>
										handleItemAction(action, cell, InventoryCellType.EXCHANGE)
									}
								/>
							))}
						</StyledInventoryGrid>
					</StyledSection>
					<StyledMoneyContainer>
						<MoneyInput
							value={exchangeMoneyAmount}
							onChange={handleExchangeMoneyAmountChange}
							placeholder="Введите сумму"
							readOnly={ready}
							min={0}
							max={Math.min(cashBalance, maxExchangeMoneyAmount)}
						/>
						<Button
							color={ready ? ButtonColors.Help : ButtonColors.Default}
							height={ButtonHeight.Height40}
							onMouseEnter={() => setButtonHover(true)}
							onMouseLeave={() => setButtonHover(false)}
							onClick={() => setReady(!ready)}
						>
							{ready ? (
								buttonHover ? (
									<Text>Отменить </Text>
								) : (
									<Text>Готов к обмену</Text>
								)
							) : (
								<Text>Я готов к обмену</Text>
							)}
						</Button>
					</StyledMoneyContainer>
					<StyledSection style={{ marginTop: 10 }}>
						<StyledSectionHeading>
							<img src={user} />
							<Text style={{ fontSize: 14, fontWeight: 500, color: '#ffffff' }}>
								{otherPlayerInfo?.nickname}
							</Text>
							<Text
								style={{
									fontSize: 16,
									color: '#ffffff',
									opacity: 0.25,
									fontStyle: 'italic',
								}}
							>
								(Предложение игрока)
							</Text>
						</StyledSectionHeading>
						<StyledInventoryGrid>
							{toExchangeCells.map((cell, index) => (
								<InventoryCell
									key={`${cell.id}-${index}`}
									item={
										cell.item
											? {
													...cell.item,
													availableActions: [],
											  }
											: undefined
									}
								/>
							))}
						</StyledInventoryGrid>
					</StyledSection>
					<StyledMoneyContainer>
						<MoneyInput
							value={exchangeToMoneyAmount}
							onChange={() => {}}
							placeholder="0"
							readOnly
						/>
						<Button
							color={
								secondPlayerReady ? ButtonColors.Help : ButtonColors.Default
							}
							mute
							height={ButtonHeight.Height40}
							disabled={!secondPlayerReady}
							style={{ pointerEvents: 'none' }}
						>
							{secondPlayerReady ? (
								<Text>Игрок готов</Text>
							) : (
								<Text>Игрок не готов</Text>
							)}
						</Button>
					</StyledMoneyContainer>
					<Button
						color={
							!ready || !secondPlayerReady
								? ButtonColors.Default
								: ButtonColors.Buy
						}
						height={ButtonHeight.Height50}
						disabled={!ready || !secondPlayerReady}
						style={{ fontSize: 16 }}
						onClick={handleExchange}
					>
						<Text>{tradeButtonText}</Text>
					</Button>
				</StyledExchangeRightContainer>
			</StyledExchangeContainer>
		</StyledContainer>
	);
};

export { InventoryExchange };
